import { useEffect, useMemo, useState } from 'react';
import { Loader2, CheckCircle2, Info } from 'lucide-react';
import { useModal } from '../../../../core/contextos/modalContext';

interface PropsComponenteLoadingProcessamento {
  peticaoId: string; // not used currently but kept for API parity
  etapaAtual?: string;
  progresso?: number; // 0-100
  tempoDecorrido?: number; // em segundos
  onProcessamentoConcluido: () => void;
  onErro?: (erro: string) => void;
}

const MENSAGENS_ETAPAS: Record<string, string> = {
  inicializando: 'Inicializando análise...',
  processando_contexto: 'Processando contexto do caso...',
  analisando_documentos: 'Analisando documentos enviados...',
  identificando_lacunas: 'Identificando informações adicionais necessárias...',
  gerando_campos: 'Preparando formulário personalizado...',
  finalizando: 'Finalizando processamento...',
};

const DICAS = [
  '💡 Quanto mais detalhes você fornecer, melhor será a análise',
  '📄 Documentos complementares aumentam a precisão das recomendações',
  '⚖️ Nossa IA analisa milhares de casos similares para gerar insights',
  '🎯 Estamos identificando os pontos mais importantes do seu caso',
];

export default function ComponenteLoadingProcessamento({
  peticaoId: _peticaoId,
  etapaAtual: etapaProp,
  progresso: progressoProp,
  tempoDecorrido: tempoDecorridoProp,
  onProcessamentoConcluido,
  onErro,
}: PropsComponenteLoadingProcessamento) {
  const { mostrarConfirmacao } = useModal();

  const [progresso, setProgresso] = useState<number>(progressoProp ?? 0);
  const [etapaIndex, setEtapaIndex] = useState<number>(0);
  const etapasKeys = useMemo(() => Object.keys(MENSAGENS_ETAPAS), []);
  const [dicaIndex, setDicaIndex] = useState<number>(0);
  const [tempo, setTempo] = useState<number>(tempoDecorridoProp ?? 0);
  const [showTimeoutPrompt, setShowTimeoutPrompt] = useState<boolean>(false);
  const [concluido, setConcluido] = useState<boolean>(false);

  // Update external provided values
  useEffect(() => {
    if (progressoProp !== undefined) setProgresso(progressoProp);
  }, [progressoProp]);

  useEffect(() => {
    if (tempoDecorridoProp !== undefined) setTempo(tempoDecorridoProp);
  }, [tempoDecorridoProp]);

  // Rotate etapa messages when not provided
  useEffect(() => {
    if (etapaProp) return;
    const id = window.setInterval(() => {
      setEtapaIndex((p) => (p + 1) % etapasKeys.length);
    }, 5000);
    return () => clearInterval(id);
  }, [etapaProp, etapasKeys]);

  // Rotate tips
  useEffect(() => {
    const id = window.setInterval(() => {
      setDicaIndex((p) => (p + 1) % DICAS.length);
    }, 8000);
    return () => clearInterval(id);
  }, []);

  // Simulate incremental progress if not provided
  useEffect(() => {
    if (progressoProp !== undefined) return;
    let active = true;
    let simulated = 0;
    const dur = 30; // seconds to reach 100
    const step = 100 / (dur * 10); // update every 100ms
    const id = window.setInterval(() => {
      if (!active) return;
      simulated = Math.min(100, simulated + step);
      setProgresso(Math.round(simulated));
    }, 100);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [progressoProp]);

  // Elapsed time ticker
  useEffect(() => {
    const id = window.setInterval(() => setTempo((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Timeout prompt (2 minutes)
  useEffect(() => {
    const id = window.setTimeout(() => {
      if (progresso < 100 && !concluido) {
        setShowTimeoutPrompt(true);
      }
    }, 2 * 60 * 1000);
    return () => clearTimeout(id);
   
  }, [progresso, concluido]);

  // Keep peticaoId prop referenced to satisfy eslint and preserve API parity
  useEffect(() => {
    void _peticaoId;
  }, [_peticaoId]);

  // Completion behavior
  useEffect(() => {
    if (progresso >= 100) {
      setConcluido(true);
      const id = window.setTimeout(() => {
        onProcessamentoConcluido();
      }, 500);
      return () => clearTimeout(id);
    }
  }, [progresso, onProcessamentoConcluido]);

  const nomeEtapa = etapaProp ?? MENSAGENS_ETAPAS[etapasKeys[etapaIndex]];
  const dicaAtual = DICAS[dicaIndex];

  const formatarTempo = (segundos: number) => {
    const m = Math.floor(segundos / 60);
    const s = segundos % 60;
    return `${m}m ${s}s`;
  };

  const onCancelar = () => {
    mostrarConfirmacao({
      titulo: 'Cancelar análise',
      mensagem: 'Tem certeza? O progresso será perdido',
      aoConfirmar: () => {
        onErro?.('cancelado_pelo_usuario');
      },
      textoConfirmar: 'Sim, cancelar',
      textoCancelar: 'Manter',
    });
  };

  // If user accepted to wait in timeout prompt, just hide prompt and continue
  useEffect(() => {
    if (!showTimeoutPrompt) return;
    mostrarConfirmacao({
      titulo: 'Processamento demorando',
      mensagem: 'O processamento está demorando mais que o esperado. Deseja continuar aguardando?',
      textoConfirmar: 'Sim, aguardar',
      textoCancelar: 'Cancelar',
      aoConfirmar: () => setShowTimeoutPrompt(false),
      aoCancelar: () => {
        setShowTimeoutPrompt(false);
        onErro?.('timeout');
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTimeoutPrompt]);

  return (
    <div role="status" aria-live="polite" className="flex h-full items-center justify-center">
      <div className="w-full max-w-md rounded-lg bg-white p-8 opacity-100 shadow-lg transition-opacity duration-300">
        <div className="flex items-center gap-4">
          {!concluido ? (
            <Loader2 className="size-8 animate-spin text-blue-500" aria-label="Processando" />
          ) : (
            <CheckCircle2 className="size-8 animate-pulse text-green-500" aria-hidden />
          )}
          <div className="flex-1">
            <h3 className="text-lg font-semibold">Analisando suas informações...</h3>
            <div className="text-sm text-gray-500">{nomeEtapa}</div>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center gap-3">
            <div className="h-2 flex-1 overflow-hidden rounded bg-gray-200" aria-label="Barra de progresso">
              <div
                className={`h-full rounded ${concluido ? 'bg-green-500' : 'bg-blue-500'}`}
                style={{ width: `${progresso}%`, transition: 'width 500ms ease' }}
              ></div>
            </div>
            <div className="w-12 text-right text-sm text-gray-700">{progresso}%</div>
          </div>
          <div className="mt-2 text-xs text-gray-500">Tempo decorrido: {formatarTempo(tempo)}</div>
        </div>

        <div className="mt-4 flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 p-3">
          <Info className="mt-1 size-5 text-blue-400" />
          <div className="text-sm text-blue-700 transition-opacity duration-300">{dicaAtual}</div>
        </div>

        <div className="mt-6 flex justify-center">
          <button
            type="button"
            className="text-sm text-gray-500 underline hover:text-gray-700"
            onClick={onCancelar}
          >
            Cancelar análise
          </button>
        </div>
      </div>
    </div>
  );
}
